import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { OrdersStatsRow, OrderCard } from './components';
import { SearchInput, ViewToggle } from '../shared';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  OrderCard as OrderCardType,
  OrdersStat,
  backendStatusToUi,
} from './salesRepOrdersTypes';
import { useOrdersStore } from '../../../store/ordersStore';
import { Order } from '../../../services/ordersApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './SalesRepOrders.module.css';

function formatCurrency(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

function formatRelativeDate(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / (60 * 1000));
  if (minutes < 60) return `Today, ${new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Today, ${new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

function adapt(o: Order): OrderCardType {
  const total = Number(o.total_amount);
  return {
    id: String(o.id),
    orderId: formatOrderId(o.id),
    customer: o.customer_name || `Customer #${o.customer_id}`,
    date: formatRelativeDate(o.order_date),
    value: formatCurrency(total),
    items: String(o.items?.length ?? 0),
    margin: '—',
    profit: formatCurrency(total * 0.18), // rough placeholder until margin is tracked
    status: backendStatusToUi(o.status),
  };
}

const SalesRepOrders: React.FC = () => {
  const [view, setView] = useState<'card' | 'grid'>('card');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [filterOpen, setFilterOpen] = useState(false);

  const items = useOrdersStore((s) => s.items);
  const loading = useOrdersStore((s) => s.loading);
  const error = useOrdersStore((s) => s.error);
  const setSource = useOrdersStore((s) => s.setSource);
  const resetFilters = useOrdersStore((s) => s.resetFilters);
  const fetchList = useOrdersStore((s) => s.fetchList);

  useEffect(() => {
    setSource('sales-rep-self');
    // Drop any filter (e.g. status='pending') left from a prior page so the
    // rep sees their full order list, not just one status bucket.
    resetFilters();
    fetchList();
  }, [setSource, resetFilters, fetchList]);

  const orders = useMemo<OrderCardType[]>(() => items.map(adapt), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      const matchesStatus = statusFilter === 'All' || o.status === statusFilter;
      const matchesQuery =
        !q || o.orderId.toLowerCase().includes(q) || o.customer.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [orders, search, statusFilter]);

  const statusOptions = ['All', 'Delivered', 'In Transit', 'Pending', 'Processing', 'Cancelled'];

  const totalValue = items.reduce((sum, o) => sum + Number(o.total_amount || 0), 0);
  const stats: OrdersStat[] = [
    { id: 'total', label: 'Total Orders', value: String(orders.length), color: 'blue', icon: 'orders' },
    { id: 'value', label: 'Total Value', value: formatCurrency(totalValue), color: 'green', icon: 'value' },
    { id: 'profit', label: 'Total Profit', value: formatCurrency(totalValue * 0.18), color: 'purple', icon: 'profit' },
    { id: 'margin', label: 'Avg Margin', value: '—', color: 'orange', icon: 'margin' },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}>All Orders</h2>
          <p className={styles.subtitle}>{orders.length} orders</p>
        </div>
        <Link to="/new-order" className={styles.newBtn}>
          + New Order
        </Link>
      </div>

      <OrdersStatsRow stats={stats} />

      <div className={styles.searchRow}>
        <div className={styles.searchCol}>
          <SearchInput
            placeholder="Search by order ID or customer..."
            value={search}
            onChange={setSearch}
          />
        </div>
        <button
          type="button"
          className={styles.filterBtn}
          onClick={() => setFilterOpen((v) => !v)}
          aria-expanded={filterOpen}
        >
          ⚙ Filter{statusFilter !== 'All' ? `: ${statusFilter}` : ''}
        </button>
        <ViewToggle value={view} onChange={setView} />
      </div>

      {filterOpen && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 8px' }}>
          {statusOptions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                border: `1px solid ${statusFilter === s ? '#2563EB' : '#E5E7EB'}`,
                background: statusFilter === s ? '#EFF6FF' : '#fff',
                color: statusFilter === s ? '#1D4ED8' : '#374151',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && filtered.length === 0}
        emptyMessage="No orders found"
        onRetry={() => fetchList()}
      >
        <div className={styles.grid}>
          {filtered.map((o) => (
            <OrderCard key={o.id} order={o} />
          ))}
        </div>
      </AsyncBoundary>
    </div>
  );
};

export const SalesRepOrdersPage: React.FC = () => {
  return <SalesRepOrders />;
};

export { SalesRepOrders };
export default SalesRepOrders;
